import { forwardRef, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';

/**
 * Funnel DS button — mirrors the marketing `ds/core` Button visuals (rounded
 * pill, Plus Jakarta Sans, named ds tokens) but is decoupled from the marketing
 * routing context: it renders a react-router `<Link>` for internal `to`, an
 * `<a>` for external/anchor hrefs, or a plain `<button>` for `onClick`. Keep the
 * class strings in sync with `src/marketing/ds/core/Button.tsx`.
 */
type Variant = 'solid' | 'solid-light' | 'outline' | 'ghost';

const BASE =
  'group inline-flex h-11 items-center rounded-full font-ds text-ds-14 font-semibold uppercase leading-none tracking-[0.02em] transition-colors duration-200 cursor-pointer select-none disabled:cursor-not-allowed disabled:opacity-40';

const VARIANTS: Record<Variant, string> = {
  solid: 'bg-ds-accent text-white hover:bg-ds-accent-hover',
  'solid-light': 'bg-white text-ds-accent hover:bg-white/90',
  outline:
    'justify-center px-6 bg-white text-ink border border-line-strong hover:border-ink-soft hover:bg-bg-subtle',
  ghost: 'px-6 bg-white/30 text-ds-on-dark backdrop-blur-[10px] hover:bg-white/40',
};

const ARROWED = new Set<Variant>(['solid', 'solid-light']);
const isExternal = (href: string) => /^(https?:|mailto:|tel:)/.test(href);

export interface ButtonProps {
  children: ReactNode;
  variant?: Variant;
  /** Internal route ("/en/confirm") → RR Link; external/anchor → <a>. */
  to?: string;
  /** Renders a <button> instead of a link. */
  onClick?: () => void;
  type?: 'button' | 'submit';
  disabled?: boolean;
  /** Extra classes (e.g. `w-full justify-between`). */
  className?: string;
  /** solid only: trailing arrow circle (default true for solid variants). */
  arrow?: boolean;
  ariaLabel?: string;
}

export const Button = forwardRef<HTMLElement, ButtonProps>(function Button(
  { children, variant = 'solid', to, onClick, type = 'button', disabled, className = '', arrow = true, ariaLabel },
  ref,
) {
  const showArrow = ARROWED.has(variant) && arrow;
  const solidPad = ARROWED.has(variant) ? (showArrow ? 'gap-6 pl-6 pr-2' : 'justify-center px-6') : '';
  const cls = `${BASE} ${VARIANTS[variant]} ${solidPad} ${className}`;
  const circleCls = variant === 'solid-light' ? 'bg-ds-accent text-white' : 'bg-white text-ds-accent';
  const arrowEl = showArrow && (
    <span className={`grid h-7 w-7 place-items-center rounded-full ${circleCls}`}>
      <ArrowUpRight
        size={15}
        strokeWidth={2.5}
        className="transition-transform duration-200 ease-out group-hover:translate-x-[1px] group-hover:-translate-y-[1px]"
      />
    </span>
  );

  const content = (
    <>
      {children}
      {arrowEl}
    </>
  );

  if (to && isExternal(to)) {
    return (
      <a ref={ref as React.Ref<HTMLAnchorElement>} href={to} target="_blank" rel="noopener noreferrer" aria-label={ariaLabel} className={cls}>
        {content}
      </a>
    );
  }
  if (to) {
    return (
      <Link ref={ref as React.Ref<HTMLAnchorElement>} to={to} aria-label={ariaLabel} className={cls}>
        {content}
      </Link>
    );
  }
  return (
    <button
      ref={ref as React.Ref<HTMLButtonElement>}
      type={type}
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      className={cls}
    >
      {content}
    </button>
  );
});
